import {
  Flex,
  VStack,
  Button,
  useDisclosure,
  Icon,
  Tooltip,
} from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useEditState from '../../../../../../common/src/hooks/useEditState';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import { UnitObject } from '../../../../types/CompanyScanTypes';
import UnitsTable from './UnitsTable';
import NewUnitModal from './NewUnitModal';
import useQuestionEnum from '../../../../hooks/useQuestionEnum';

interface Props {
  question: FlowsQuestion;
}

const UnitsInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const { toBeEdited, setToBeEdited, editIndex, setEditIndex } =
    useEditState<UnitObject>();

  const {
    field: { value, onBlur },
  } = useFlowInputComponent({ question, defaultValue: [] });

  const units = (value as UnitObject[] | undefined) || [];

  const addUnit = (unit: UnitObject) => {
    onBlur({ name: question.id, value: [...units, unit] });
  };

  const editUnit = (unit: UnitObject, index: number) => {
    if (index > -1) {
      const newUnits = [...units];
      newUnits[index] = unit;
      onBlur({ name: question.id, value: newUnits });
    }
  };

  const handleOnRemove = (index: number) => {
    onBlur({
      name: question.id,
      value: units.filter((_, _index) => _index !== index),
    });
  };

  const handleOnEdit = (unit: UnitObject, index: number) => {
    setToBeEdited(unit);
    setEditIndex(index);
    onOpen();
  };

  const handleOnClose = () => {
    setToBeEdited(undefined);
    setEditIndex(undefined);
    onClose();
  };

  const options = useQuestionEnum(question);
  const selectableOptions = useQuestionEnum(question).filter(
    ({ value }) => !units.map(({ unit }) => unit).includes(value),
  );

  return (
    <VStack spacing="4" align="stretch">
      {isOpen && (
        <NewUnitModal
          options={selectableOptions}
          unit={toBeEdited}
          isOpen={isOpen}
          onClose={handleOnClose}
          addUnit={addUnit}
          editUnit={(unit) => editUnit(unit, editIndex || 0)}
        />
      )}
      <UnitsTable
        options={options}
        units={units}
        onEdit={handleOnEdit}
        onRemove={handleOnRemove}
      />
      <Flex justify="flex-end">
        <Tooltip
          label={t('FLOWS_COMPANY-SCAN_UNITS-INPUT_ALL-ADDED')}
          isDisabled={
            selectableOptions.length > 0 && !question.rules.is_disabled
          }
          shouldWrapChildren
        >
          <Button
            onClick={onOpen}
            leftIcon={<Icon name="GeneralActionsAdd" />}
            isDisabled={
              selectableOptions.length === 0 || question.rules.is_disabled
            }
          >
            {t('FLOWS_COMPANY-SCAN_UNITS-INPUT_ADD-UNIT')}
          </Button>
        </Tooltip>
      </Flex>
    </VStack>
  );
};

export default UnitsInputComponent;
